import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Gift, Copy, Check, Users, CreditCard, Share2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralCard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/referrals/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };

  const copyCode = async () => {
    const code = user?.referral_code || stats?.referral_code;
    if (!code) return;
    
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t('referral.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const shareLink = () => {
    const code = user?.referral_code || stats?.referral_code;
    const shareUrl = `${window.location.origin}/register?ref=${code}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'CloudTexa',
        text: `¡Únete a CloudTexa con mi código ${code} y obtén créditos gratis!`,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  const referralCode = user?.referral_code || stats?.referral_code || '';
  const referralCount = stats?.referral_count || user?.referral_count || 0;
  const creditsEarned = stats?.total_credits_earned || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6 lg:p-8"
      data-testid="referral-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500/20 to-yellow-500/20">
          <Gift className="w-6 h-6 text-orange-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{t('referral.title')}</h3>
          <p className="text-sm text-gray-400">{t('referral.subtitle')}</p>
        </div>
      </div>

      {/* Referral Code Display */}
      <div className="mb-6">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
          {t('referral.yourCode')}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 font-mono text-xl font-bold text-white tracking-wider">
            {referralCode}
          </div>
          <button
            onClick={copyCode}
            className={`p-3 rounded-xl transition-all ${
              copied 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
            data-testid="copy-referral-btn"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          </button>
          <button
            onClick={shareLink}
            className="p-3 rounded-xl bg-[#FF6B00] text-white hover:bg-[#E65A00] transition-colors"
            data-testid="share-referral-btn"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4 text-center">
          <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{referralCount}</p>
          <p className="text-xs text-gray-500">{t('referral.friendsReferred')}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 text-center">
          <CreditCard className="w-5 h-5 text-green-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{creditsEarned}</p>
          <p className="text-xs text-gray-500">{t('referral.creditsEarned')}</p>
        </div>
      </div>

      {/* Bonus Info */}
      <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 rounded-xl p-4 border border-orange-500/20">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <p className="text-xs text-gray-400 mb-1">{t('referral.youGet')}</p>
            <p className="text-lg font-bold text-orange-400">+15 créditos</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
          <div className="text-center flex-1">
            <p className="text-xs text-gray-400 mb-1">{t('referral.friendGets')}</p>
            <p className="text-lg font-bold text-green-400">+5 créditos</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-6 pt-6 border-t border-white/10">
        <p className="text-sm font-semibold text-white mb-3">{t('referral.howItWorks')}</p>
        <div className="space-y-2 text-sm text-gray-400">
          <p className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-400 text-xs flex items-center justify-center font-bold">1</span>
            {t('referral.step1')}
          </p>
          <p className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-400 text-xs flex items-center justify-center font-bold">2</span>
            {t('referral.step2')}
          </p>
          <p className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-400 text-xs flex items-center justify-center font-bold">3</span>
            {t('referral.step3')}
          </p>
        </div>
      </div>
    </motion.div>
  );
};
